import EventEmitter from 'events';
import LoggerWrapper from '../../modules/logger-wrapper';
import { getSafeAudioContext, convertToMonoWithSilence } from './helpers';
// eslint-disable-next-line
import rawPcmWorkletUrl from 'worklet-loader!./worklets/raw-pcm.worklet.js';
import Devices from './Devices';

const logger = new LoggerWrapper('MEDIA_CORE', 'SafeAudioTrackWithBackup', LoggerWrapper.DEBUG);
logger.setRemoteVerbosity(LoggerWrapper.DEBUG);
class SafeAudioTrackWithBackup extends EventEmitter {
	originalTrack = null;
	track = null;
	sourceNode = null;
	stateChangeWatcher;

	constructor(track = null) {
		super();
		this.originalTrack = track;
		this.audioContext = getSafeAudioContext();
		this.stateChangeWatcher = this.resetStateChangeWatcher();
	}

	/**
	 * Starts the raw PCM worklet. Add module to AudioContext if not there.
	 * @returns {Promise<AudioWorkletNode>}
	 */
	async _startPcmNode() {
		let pcmNode;

		try {
			pcmNode = new AudioWorkletNode(this.audioContext, 'raw-pcm', {
				numberOfOutputs: 1,
				numberOfInputs: 2,
			});
		} catch (err) {
			await this.audioContext.audioWorklet.addModule(rawPcmWorkletUrl);
			pcmNode = new AudioWorkletNode(this.audioContext, 'raw-pcm', {
				numberOfOutputs: 1,
				numberOfInputs: 2,
			});
		}

		return pcmNode;
	}

	async boot() {
		//const pcmNode = new GainNode(this.audioContext);
		const pcmNode = await this._startPcmNode();
		//we can use a simpler approach to determine speaker
		let zcr = 0;
		let ste = 0;
		let bufferLen = 0;
		pcmNode.port.onmessage = (e) => {
			if (e.data.type === 'data') {
				const rawData = e.data.payload;
				this.emit('audio:raw', rawData);
				if (bufferLen > 2048) {
					let w = (1 / bufferLen) * ste * (1 - zcr) * 1000;
					const rms = Math.sqrt(ste / bufferLen);
					//you can use the rms for vue meter
					ste = 0;
					zcr = 0;
					bufferLen = 0;
					if (w > 3) {
						//this can be much smarter, we can look for average and variance to determine the threshold
						//but simpler and less intensive than fft
						//console.log('talking');
					}
				}
				bufferLen += rawData.buffer.byteLength / 4;
				zcr += rawData.zcr;
				ste += rawData.ste;
			} else if (e.data.type === 'log') {
				logger[e.data.payload.type || 'debug'](e.data.payload.message);
			}
		};

		// Creating origin node with base silence.
		const mediaStream = new MediaStream([this.originalTrack]);
		const originSourceNode = this.audioContext.createMediaStreamSource(mediaStream);
		this.sourceNode = convertToMonoWithSilence(originSourceNode, this.audioContext);

		this.sourceNode.connect(pcmNode, 0, 0);

		// Create backup track using a different backup device.
		const backupMediaStream = await this._requestBackupMediaStream();
		if (backupMediaStream) {
			const backupSourceNode = this.audioContext.createMediaStreamSource(backupMediaStream);
			const backupMonoNode = convertToMonoWithSilence(backupSourceNode, this.audioContext);
			backupMonoNode.connect(pcmNode, 0, 1);
		}

		// Creating destination mediaStream/AudioNode
		const dest = new MediaStreamAudioDestinationNode(this.audioContext, {
			channelCount: 1,
			channelCountMode: 'explicit',
		});

		pcmNode.connect(dest);
		this.track = dest.stream.getAudioTracks()[0];

		this.audioContext.addEventListener('statechange', this.handleAudioContextState);
	}

	handleAudioContextState = (e) => {
		if (e.target.state === 'suspended') {
			// more than 10 seconds since last time the audio context suspended than reset watcher or first suspend
			if (
				this.stateChangeWatcher.lastTimeStamp === 0 ||
				e.timeStamp - this.stateChangeWatcher.lastTimeStamp > this.stateChangeWatcher.threshold.time
			) {
				this.stateChangeWatcher.firstTimeStamp = e.timeStamp;
			}
			// in the last 10 seconds the audio context suspended more than threshold
			if (
				e.timeStamp - this.stateChangeWatcher.firstTimeStamp > this.stateChangeWatcher.threshold.time &&
				this.stateChangeWatcher.count > this.stateChangeWatcher.threshold.count
			) {
				logger.warn(`AudioContext suspended more than declared threshold`, {
					audioContext: e.target,
					watcherSettings: this.stateChangeWatcher,
				});
				this.emit('audio:suspended', e.target.state);
			}

			logger.debug('AudioContext suspended', {
				state: e.target.state,
				audioContext: e.target,
			});

			this.stateChangeWatcher = {
				...this.stateChangeWatcher,
				count: ++this.stateChangeWatcher.count,
				lastTimeStamp: e.timeStamp,
			};
			this.audioContext.resume().then(
				() => {
					logger.debug('AudioContext resume success', {
						audioContext: e.target,
					});
				},
				(err) => {
					logger.error('Failed resume audioContext', {
						audioContext: e.target,
						error: err,
					});
				}
			);
		}
	};

	async _requestBackupMediaStream() {
		const currentDeviceId = this.originalTrack.getSettings().deviceId;

		const possibleBackupDevices = await (
			await Devices.requestDeviceList()
		).filter((d) => d.deviceId !== 'default' && d.kind === 'audioinput' && d.deviceId !== currentDeviceId);

		const currentConstraints = this.originalTrack.getConstraints();

		try {
			// Tries to find the built-in audio device.
			let backupDevice = possibleBackupDevices.find((d) => d.label?.toLowerCase().includes('built-in'));

			if (!backupDevice && possibleBackupDevices.length > 0) {
				// If no built-in audio device is found, tries to find the first available audio device.
				backupDevice = possibleBackupDevices[0];
			} else if (!backupDevice && possibleBackupDevices.length === 0) {
				logger.warn('No devices available to use as backup audio device.');
				return;
			}

			const backupAudioStream = await navigator.mediaDevices.getUserMedia({
				audio: {
					...currentConstraints,
					deviceId: { exact: backupDevice.deviceId },
				},
				video: false,
			});

			const backupAudioTrack = backupAudioStream?.getAudioTracks()[0];
			if (backupAudioStream && backupAudioTrack) {
				logger.debug(`Got background audio stream [${backupAudioTrack.label}]`, {
					settings: backupAudioTrack.getSettings(),
				});
			}

			return backupAudioStream;
		} catch (err) {
			logger.warn('Failed to get backup audio stream', null, { err });
		}
	}

	resetStateChangeWatcher() {
		return {
			firstTimeStamp: 0,
			lastTimeStamp: 0,
			count: 0,
			threshold: { count: 3, time: 10000 },
		};
	}

	stop() {
		logger.debug(`Stopping safe audio track by request [${this.track?.label}]`);
		this.track?.stop();
		this.sourceNode?.disconnect();
		this.audioContext.removeEventListener('statechange', this.handleAudioContextState);
	}
}

export default SafeAudioTrackWithBackup;
